import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { costingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Calculator, Eye, Copy, Loader2, Search, Boxes, Package,
} from 'lucide-react';
import { PaginationControls } from '../components/PaginationControls';
import { Checkbox } from '../components/ui/checkbox';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionBar } from '../components/bulk/BulkActionBar';

const COSTING_CSV_COLUMNS = [
  { header: 'Costing No', value: (row) => row.costing_number },
  { header: 'Part No', value: (row) => row.part_internal_no },
  { header: 'Description', value: (row) => row.part_description },
  { header: 'Method', value: (row) => row.costing_method },
  { header: 'Cost/Piece', value: (row) => row.total_cost_per_piece },
  { header: 'Selling Price', value: (row) => row.selling_price_per_piece },
  { header: 'Margin %', value: (row) => row.margin_percentage },
  { header: 'Status', value: (row) => row.status },
];

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
  locked: 'bg-purple-100 text-purple-700',
};

// Costings List Page
export default function CostingsPage() {
  const [costings, setCostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const { isManager } = useAuth();
  const [limit, setLimit] = useState(50);
  const selection = useBulkSelection(costings);

  const fetchCostings = useCallback(async () => {
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const response = await costingsAPI.list(params);
      const items = response.data?.items || response.data?.data || [];
      setCostings(items);
      selection.remember(items);
      setTotal(response.data?.total || 0);
      setTotalPages(response.data?.total_pages || 1);
    } catch (error) {
      toast.error('Failed to fetch costings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, limit]);

  useEffect(() => {
    fetchCostings();
  }, [fetchCostings]);

  const handleClone = async (costingId) => {
    try {
      const response = await costingsAPI.clone(costingId);
      toast.success(`Costing cloned as version ${response.data.version}`);
      fetchCostings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Clone failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
            Costings
          </h1>
          <p className="text-muted-foreground">Product cost calculations (Part-Centric)</p>
        </div>
        <Button 
          className="btn-primary" 
          onClick={() => navigate('/costings/new')}
          data-testid="new-costing-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Costing
        </Button>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part no, description, customer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="costing-search-input"
              />
            </div>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px]" data-testid="costing-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
              </SelectContent>
            </Select>
            <Badge variant="outline">{total} costings</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <BulkActionBar
            entity="costings"
            selectedIds={selection.selectedIds}
            selectedRows={selection.selectedRows}
            onClear={selection.clear}
            onDone={fetchCostings}
            csvColumns={COSTING_CSV_COLUMNS}
            csvPrefix="costings"
          />
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : costings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No costings found</p>
              <p className="text-sm">Create a costing for a part to get started</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={selection.allOnPageSelected}
                        onCheckedChange={selection.togglePage}
                        aria-label="Select all costings on this page"
                        data-testid="costings-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Costing No</TableHead>
                    <TableHead>Part No</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Cost/Piece</TableHead>
                    <TableHead>Selling Price</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costings.map((costing) => (
                    <TableRow key={costing.id} className="table-row" data-state={selection.isSelected(costing.id) ? 'selected' : undefined}>
                      <TableCell>
                        <Checkbox
                          checked={selection.isSelected(costing.id)}
                          onCheckedChange={() => selection.toggleOne(costing)}
                          aria-label={`Select costing ${costing.costing_number}`}
                          data-testid={`costings-row-checkbox-${costing.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono">{costing.costing_number || `V${costing.version_number}`}</TableCell>
                      <TableCell className="font-mono font-medium">
                        {costing.part_internal_no}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {costing.part_description}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {costing.costing_method === 'bom_rollup' ? (
                            <><Boxes className="h-3 w-3 mr-1" />BOM</>
                          ) : (
                            <><Package className="h-3 w-3 mr-1" />Process</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">
                        ₹{costing.total_cost_per_piece?.toFixed(4)}
                      </TableCell>
                      <TableCell className="font-mono text-primary font-bold">
                        ₹{costing.selling_price_per_piece?.toFixed(4)}
                      </TableCell>
                      <TableCell className="font-mono">
                        {costing.margin_percentage}%
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[costing.status]}>
                          {costing.status?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/costings/${costing.id}`)}
                            data-testid={`view-costing-${costing.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!costing.is_locked && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleClone(costing.id)}
                              title="Clone for re-costing"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onLimitChange={setLimit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

